import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../../../components/Buttons/Button/Button';
import Combobox from '../../../components/Combobox/Combobox';
import './BulkEditModal.css';

const BulkEditModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  selectedCount, 
  roleOptions = [] 
}) => {
  const { t } = useTranslation('userManagement');
  const [formData, setFormData] = useState({
    role_id: '',
    is_active: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!isOpen) {
      // Reset form when modal closes
      setFormData({
        role_id: '',
        is_active: ''
      });
      setErrors({});
    }
  }, [isOpen]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      // Build field_updates object with only non-empty values
      const fieldUpdates = {};
      
      if (formData.role_id !== '') {
        fieldUpdates.role_id = parseInt(formData.role_id);
      }
      
      if (formData.is_active !== '') {
        fieldUpdates.is_active = formData.is_active === 'true';
      }
      
      // Only proceed if there are fields to update
      if (Object.keys(fieldUpdates).length === 0) {
        setErrors({ general: t('bulkEdit.errors.selectAtLeastOne') });
        return;
      }
      
      await onSave(fieldUpdates);
    } catch (error) {
      setErrors({ general: error.message || t('bulkEdit.errors.updateError') });
    } finally {
      setLoading(false);
    }
  };


  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container bulk-edit-modal">
        <div className="modal-header">
          <h2 className="modal-title">
            {t('bulkEdit.title', { count: selectedCount })}
          </h2>
          <button
            onClick={onClose}
            className="modal-close-button"
            aria-label={t('bulkEdit.aria.close')}
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {errors.general && (
            <div className="error-message mb-4">
              {errors.general}
            </div>
          )}

          <div className="form-section">
            <p className="text-sm text-gray-600 mb-4">
              {t('bulkEdit.description')}
            </p>

            {/* Role */}
            <div className="form-group">
              <label className="form-label">
                {t('bulkEdit.fields.role')}
              </label>
              <Combobox
                value={formData.role_id}
                onChange={(value) => handleInputChange('role_id', value)}
                options={[
                  { value: '', label: t('bulkEdit.options.noChange') },
                  ...roleOptions
                ]}
                placeholder={t('bulkEdit.placeholders.selectRole')}
                buttonClass="form-input"
              />
            </div>

            {/* Is Active */}
            <div className="form-group">
              <label className="form-label">
                {t('bulkEdit.fields.activeStatus')}
              </label>
              <Combobox
                value={formData.is_active}
                onChange={(value) => handleInputChange('is_active', value)}
                options={[
                  { value: '', label: t('bulkEdit.options.noChange') },
                  { value: 'true', label: t('bulkEdit.options.active') },
                  { value: 'false', label: t('bulkEdit.options.inactive') }
                ]}
                placeholder={t('bulkEdit.placeholders.selectStatus')}
                buttonClass="form-input"
              />
            </div>
          </div>

          <div className="modal-footer">
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              disabled={loading}
            >
              {t('bulkEdit.buttons.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={loading}
              leftIcon={<Save size={16} />}
            >
              {loading ? t('bulkEdit.buttons.updating') : t('bulkEdit.buttons.update', { count: selectedCount })}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BulkEditModal;
